/**
 * PuzzlePlayLogic.js
 * 
 * Pure game logic functions for puzzle word search operations.
 * These functions contain no UI dependencies and can be easily tested.
 * 
 * Common functions shared between Grid (Word Search/Hidden Message) and Crossword puzzles.
 */

/**
 * Removes hyphens from a word for comparison
 * @param {string} word - The word to process
 * @returns {string} - The word without hyphens
 */
export const removeHyphens = (word) => {
    if (!word || typeof word !== 'string') {
        return '';
    }
    return word.replace(/-/g, '');
};

/**
 * Removes spaces from a word for comparison (e.g. "fer à repasser" -> "feràrepasser")
 * @param {string} word - The word to process
 * @returns {string} - The word without spaces
 */
const removeSpaces = (word) => {
    if (!word || typeof word !== 'string') {
        return '';
    }
    return word.replace(/\s+/g, '');
};

/**
 * Searches for a complete word in filledCoordinates.
 * Matches coord.text (grid letters, often no spaces), coord.word (display form, may have spaces), forward and reversed.
 * @param {string} word - The word to search for (e.g. "fer à repasser" or "ferarepasser")
 * @param {Array} filledCoordinates - Array of coordinate objects from puzzle data
 * @returns {Array|null} - Array of {row, col} positions if found, null otherwise
 */
export const findWordInGrid = (word, filledCoordinates) => {
    if (!word || !filledCoordinates) {
        return null;
    }

    const normalizedWord = word.toLowerCase().trim();
    if (normalizedWord.length === 0) {
        return null;
    }

    // Remove hyphens/spaces and normalize French characters for comparison
    const wordWithoutHyphens = removeHyphens(normalizedWord);
    const wordNormalized = normalizeFrenchCharacters(normalizedWord);
    const wordNormalizedWithoutHyphens = removeHyphens(wordNormalized);
    const wordNoSpaces = removeSpaces(normalizedWord);
    const wordNormalizedNoSpaces = removeSpaces(wordNormalized);
    const wordNoSpacesWithoutHyphens = removeHyphens(wordNoSpaces);
    const wordNormalizedNoSpacesWithoutHyphens = removeHyphens(wordNormalizedNoSpaces);

    for (const coord of filledCoordinates) {
        // Use coord.text (grid letters) and/or coord.word (display form, e.g. "fer à repasser")
        const coordText = (coord.text || '').toLowerCase().trim();
        const coordDisplay = (coord.word != null && String(coord.word).trim() !== '')
            ? String(coord.word).toLowerCase().trim()
            : coordText;
        if (!coordText && !coordDisplay) continue;

        const coordWord = coordText || removeSpaces(coordDisplay);
        const coordWordNormalized = normalizeFrenchCharacters(coordWord);
        const coordWordWithoutHyphens = removeHyphens(coordWord);
        const coordWordNormalizedWithoutHyphens = removeHyphens(coordWordNormalized);
        const coordWordNoSpaces = removeSpaces(coordWord);
        const coordDisplayNoSpaces = removeSpaces(coordDisplay);
        const coordDisplayNormalized = normalizeFrenchCharacters(coordDisplay);
        const coordDisplayNoSpacesNormalized = removeSpaces(coordDisplayNormalized);

        const reversedCoordWord = coord.reversed ? coordWord.split('').reverse().join('') : coordWord;
        const reversedCoordWordNormalized = coord.reversed ? coordWordNormalized.split('').reverse().join('') : coordWordNormalized;
        const reversedCoordWordWithoutHyphens = removeHyphens(reversedCoordWord);
        const reversedCoordWordNormalizedWithoutHyphens = removeHyphens(reversedCoordWordNormalized);

        const matches = (a, b) => a && b && a === b;
        // User word (with/without spaces, hyphens, accents) vs coord grid form (text) and display form (word)
        if (matches(normalizedWord, coordWord) || matches(normalizedWord, reversedCoordWord) ||
            matches(normalizedWord, coordDisplay) || matches(normalizedWord, coordDisplayNoSpaces) ||
            matches(wordNormalized, coordWordNormalized) || matches(wordNormalized, reversedCoordWordNormalized) ||
            matches(wordNormalized, coordDisplayNormalized) || matches(wordNormalized, coordDisplayNoSpacesNormalized) ||
            matches(wordWithoutHyphens, coordWordWithoutHyphens) || matches(wordWithoutHyphens, reversedCoordWordWithoutHyphens) ||
            matches(wordNormalizedWithoutHyphens, coordWordNormalizedWithoutHyphens) ||
            matches(wordNormalizedWithoutHyphens, reversedCoordWordNormalizedWithoutHyphens) ||
            matches(wordNoSpaces, coordWord) || matches(wordNoSpaces, reversedCoordWord) ||
            matches(wordNoSpaces, coordWordNoSpaces) || matches(wordNormalizedNoSpaces, coordWordNormalized) ||
            matches(wordNoSpacesWithoutHyphens, coordWordWithoutHyphens) ||
            matches(wordNormalizedNoSpacesWithoutHyphens, coordWordNormalizedWithoutHyphens)) {
            if (coord.positions && coord.positions.length > 0) {
                return coord.positions.map(pos => ({
                    row: pos.row,
                    col: pos.col
                }));
            }
        }
    }

    return null;
};

/**
 * Finds all complete connected paths in straight lines for a word
 * Only shows cells that form straight lines (horizontal, vertical, or diagonal)
 * @param {string} word - The word to find paths for
 * @param {Array<Array<string>>} grid - The puzzle grid (2D array)
 * @returns {Array} - Array of {row, col, charIndex} objects representing valid paths
 */
export const findConnectedPaths = (word, grid) => {
    if (!word || !grid || word.length === 0) {
        return [];
    }

    const paths = [];
    const normalizedWord = word.toLowerCase();

    // Try all starting positions for the first character
    grid.forEach((startRow, startRowIndex) => {
        startRow.forEach((cell, startColIndex) => {
            if (cell && cell.toLowerCase() === normalizedWord[0]) {
                // Try all 8 directions from this starting position
                const directions = [
                    { dr: -1, dc: -1 },  // diagonal-left-up
                    { dr: -1, dc: 0 },   // vertical-up
                    { dr: -1, dc: 1 },   // diagonal-right-up
                    { dr: 0, dc: -1 },   // horizontal-left
                    { dr: 0, dc: 1 },    // horizontal-right
                    { dr: 1, dc: -1 },   // diagonal-left-down
                    { dr: 1, dc: 0 },    // vertical-down
                    { dr: 1, dc: 1 }     // diagonal-right-down
                ];

                // Try each direction
                directions.forEach(direction => {
                    const path = [];
                    let currentRow = startRowIndex;
                    let currentCol = startColIndex;
                    const visited = new Set();

                    // Check if we can form the entire word in this direction
                    for (let charIndex = 0; charIndex < normalizedWord.length; charIndex++) {
                        const charToFind = normalizedWord[charIndex];

                        // Check bounds
                        if (currentRow < 0 || currentRow >= grid.length ||
                            currentCol < 0 || currentCol >= grid[currentRow].length) {
                            break;
                        }

                        // Check if cell matches and hasn't been visited in this path
                        const key = `${currentRow}-${currentCol}`;
                        if (visited.has(key)) {
                            break; // Can't reuse cells
                        }

                        const cell = grid[currentRow][currentCol];
                        if (cell && cell.toLowerCase() === charToFind) {
                            visited.add(key);
                            path.push({
                                row: currentRow,
                                col: currentCol,
                                charIndex: charIndex
                            });

                            // Move to next position in the same direction
                            if (charIndex < normalizedWord.length - 1) {
                                currentRow += direction.dr;
                                currentCol += direction.dc;
                            }
                        } else {
                            break; // Character doesn't match
                        }
                    }

                    // If we found a complete path, add it
                    if (path.length === normalizedWord.length) {
                        paths.push(path);
                    }
                });
            }
        });
    });

    // Flatten all paths into a set of cells with their character indices
    const cellMap = new Map();
    paths.forEach(path => {
        path.forEach(pos => {
            const key = `${pos.row}-${pos.col}`;
            // Keep the character index for coloring (or update if we find a better match)
            if (!cellMap.has(key) || cellMap.get(key).charIndex > pos.charIndex) {
                cellMap.set(key, {
                    row: pos.row,
                    col: pos.col,
                    charIndex: pos.charIndex
                });
            }
        });
    });

    return Array.from(cellMap.values());
};

/**
 * Finds all matching cells as user types (shows only adjacent cells for subsequent characters)
 * @param {string} typedText - The text currently typed by the user
 * @param {Array<Array<string>>} grid - The puzzle grid (2D array)
 * @returns {Array} - Array of {row, col, charIndex} objects representing matching cells
 */
export const findMatchingCellsAsTyped = (typedText, grid) => {
    if (!typedText || !grid) {
        return [];
    }

    const normalizedText = typedText.toLowerCase().trim();
    if (normalizedText.length === 0) {
        return [];
    }

    // First character: show ALL instances
    if (normalizedText.length === 1) {
        const charToFind = normalizedText[0];
        const matchingCells = [];
        grid.forEach((row, rowIndex) => {
            row.forEach((cell, colIndex) => {
                if (cell && cell.toLowerCase() === charToFind) {
                    matchingCells.push({
                        row: rowIndex,
                        col: colIndex,
                        charIndex: 0
                    });
                }
            });
        });
        return matchingCells;
    }

    // For multiple characters: find all connected paths
    return findConnectedPaths(normalizedText, grid);
};

/**
 * Checks if a word matches any word in the puzzle words list (forward or reversed)
 * @param {string} word - The word to check
 * @param {Array<string>} puzzleWords - Array of valid puzzle words
 * @returns {string|null} - The matching puzzle word (original case) or null if no match
 */
export const findMatchingWord = (word, puzzleWords) => {
    if (!word || !puzzleWords || puzzleWords.length === 0) {
        return null;
    }

    const wordLower = word.toLowerCase();
    const wordNormalized = normalizeFrenchCharacters(wordLower);
    const wordWithoutHyphens = removeHyphens(wordLower);
    const wordNormalizedWithoutHyphens = removeHyphens(wordNormalized);
    const reversedWord = wordLower.split('').reverse().join('');
    const reversedWordNormalized = wordNormalized.split('').reverse().join('');
    const reversedWordWithoutHyphens = removeHyphens(reversedWord);
    const reversedWordNormalizedWithoutHyphens = removeHyphens(reversedWordNormalized);

    const wordNoSpaces = removeSpaces(wordLower);
    const wordNormalizedNoSpaces = removeSpaces(wordNormalized);

    const matchingWord = puzzleWords.find(puzzleWord => {
        const puzzleWordLower = puzzleWord.toLowerCase();
        const puzzleWordNormalized = normalizeFrenchCharacters(puzzleWordLower);
        const puzzleWordWithoutHyphens = removeHyphens(puzzleWordLower);
        const puzzleWordNormalizedWithoutHyphens = removeHyphens(puzzleWordNormalized);
        const puzzleWordNoSpaces = removeSpaces(puzzleWordLower);
        const puzzleWordNormalizedNoSpaces = removeSpaces(puzzleWordNormalized);
        const reversedPuzzleWord = puzzleWordLower.split('').reverse().join('');
        const reversedPuzzleWordNormalized = puzzleWordNormalized.split('').reverse().join('');
        const reversedPuzzleWordWithoutHyphens = removeHyphens(reversedPuzzleWord);
        const reversedPuzzleWordNormalizedWithoutHyphens = removeHyphens(reversedPuzzleWordNormalized);

        // Compare with/without hyphens, spaces, accents (forward and reversed)
        return puzzleWordLower === wordLower ||
               puzzleWordLower === reversedWord ||
               puzzleWordNormalized === wordNormalized ||
               puzzleWordNormalized === reversedWordNormalized ||
               puzzleWordWithoutHyphens === wordWithoutHyphens ||
               puzzleWordWithoutHyphens === reversedWordWithoutHyphens ||
               puzzleWordNormalizedWithoutHyphens === wordNormalizedWithoutHyphens ||
               puzzleWordNormalizedWithoutHyphens === reversedWordNormalizedWithoutHyphens ||
               reversedPuzzleWordWithoutHyphens === wordWithoutHyphens ||
               reversedPuzzleWordNormalizedWithoutHyphens === wordNormalizedWithoutHyphens ||
               puzzleWordNoSpaces === wordNoSpaces ||
               puzzleWordNormalizedNoSpaces === wordNormalizedNoSpaces;
    });

    return matchingWord || null;
};

/**
 * Validates if a typed word forms a valid path in the grid
 * @param {string} word - The word to validate
 * @param {Array<Array<string>>} grid - The puzzle grid (2D array)
 * @param {Array} filledCoordinates - Array of coordinate objects from puzzle data
 * @returns {Object} - {isValid: boolean, cells: Array|null} - Validation result and cell positions
 */
export const validateWordPath = (word, grid, filledCoordinates) => {
    if (!word || !grid) {
        return { isValid: false, cells: null };
    }

    const normalizedWord = word.toLowerCase().trim();
    if (normalizedWord.length === 0) {
        return { isValid: false, cells: null };
    }

    // First check filledCoordinates
    const cellsFromGrid = findWordInGrid(normalizedWord, filledCoordinates);
    if (cellsFromGrid) {
        return { isValid: true, cells: cellsFromGrid };
    }

    // Then check if we can form a valid path (straight line)
    const validPathCells = findMatchingCellsAsTyped(normalizedWord, grid);
    const hasValidPath = validPathCells.length > 0 && 
        validPathCells.length === normalizedWord.length &&
        validPathCells.every(cell => cell.charIndex !== undefined);

    if (hasValidPath) {
        // Convert to simple {row, col} format
        const cells = validPathCells.map(c => ({ row: c.row, col: c.col }));
        return { isValid: true, cells: cells };
    }

    return { isValid: false, cells: null };
};

/**
 * Checks if a word is already in the found words list
 * @param {string} word - The word to check (will be normalized to lowercase)
 * @param {Array<string>} wordsFound - Array of already found words
 * @returns {boolean} - True if word is already found
 */
export const isWordAlreadyFound = (word, wordsFound) => {
    if (!word || !wordsFound) {
        return false;
    }

    const normalizedWord = word.toLowerCase().trim();
    const normalizedWordFrench = normalizeFrenchCharacters(normalizedWord);
    const wordWithoutHyphens = removeHyphens(normalizedWord);
    const wordNormalizedWithoutHyphens = removeHyphens(normalizedWordFrench);
    
    // Check both with and without hyphens, with and without accent normalization
    return wordsFound.some(foundWord => {
        const foundWordLower = foundWord.toLowerCase().trim();
        const foundWordNormalized = normalizeFrenchCharacters(foundWordLower);
        const foundWordWithoutHyphens = removeHyphens(foundWordLower);
        const foundWordNormalizedWithoutHyphens = removeHyphens(foundWordNormalized);
        return foundWordLower === normalizedWord || 
               foundWordNormalized === normalizedWordFrench ||
               foundWordWithoutHyphens === wordWithoutHyphens ||
               foundWordNormalizedWithoutHyphens === wordNormalizedWithoutHyphens;
    });
};

/**
 * Normalizes a word by removing accents (for French characters like é -> e)
 * @param {string} word - The word to normalize
 * @returns {string} - The normalized word (accents removed, lowercase)
 */
export const normalizeFrenchCharacters = (word) => {
    if (!word || typeof word !== 'string') {
        return '';
    }
    return word
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .toLowerCase()
        .trim();
};

/**
 * Normalizes a word to lowercase for consistent comparison
 * @param {string} word - The word to normalize
 * @returns {string} - The normalized word (lowercase)
 */
export const normalizeWord = (word) => {
    if (!word || typeof word !== 'string') {
        return '';
    }
    return word.toLowerCase().trim();
};

/**
 * Set of "row,col" keys for every cell belonging to a found word (intersections count once).
 */
export const getFoundCellKeySetFromWords = (filledCoordinates, wordsFound) => {
    const set = new Set();
    if (!filledCoordinates?.length || !wordsFound?.length) {
        return set;
    }
    const normalizedFound = new Set(
        wordsFound
            .map((w) => normalizeWord(typeof w === "string" ? w : String(w)))
            .filter(Boolean)
    );
    filledCoordinates.forEach((coord) => {
        const nw = normalizeWord(coord.text || coord.word || "");
        if (!nw || !normalizedFound.has(nw)) {
            return;
        }
        coord.positions?.forEach((p) => {
            if (typeof p.row === "number" && typeof p.col === "number") {
                set.add(`${p.row},${p.col}`);
            }
        });
    });
    return set;
};

/**
 * Builds a hint pattern (e.g. ---A---) from hint cells + cells already revealed by found intersecting words.
 * @param {string} expectedWord - Word text (may include hyphens)
 * @param {{row:number,col:number}[]} wordCells - Cells in reading order, same length as letters after removeHyphens
 * @param {{row:number,col:number}[]} hintRevealCells - Cells where a hint letter is shown (may be empty)
 * @param {Set<string>} [foundCellKeySet] - Keys "row,col" for cells that belong to any found word
 * @returns {string|null}
 */
export const getHintPatternForWordCells = (expectedWord, wordCells, hintRevealCells, foundCellKeySet) => {
    if (!expectedWord || !wordCells?.length) {
        return null;
    }
    const plain = removeHyphens(normalizeWord(expectedWord)).toUpperCase();
    if (!plain.length) {
        return null;
    }
    const hintSet = new Set((hintRevealCells || []).map((c) => `${c.row},${c.col}`));
    const foundSet = foundCellKeySet instanceof Set ? foundCellKeySet : new Set();
    let pattern = "";
    for (let i = 0; i < plain.length; i += 1) {
        const pos = wordCells[i];
        if (!pos || typeof pos.row !== "number" || typeof pos.col !== "number") {
            pattern += "-";
            continue;
        }
        const key = `${pos.row},${pos.col}`;
        const fromHint = hintSet.has(key);
        const fromFoundWord = foundSet.has(key);
        pattern += fromHint || fromFoundWord ? plain[i] : "-";
    }
    return pattern;
};

/** True if pattern has at least one revealed (non-dash) letter */
export const hintPatternHasRevealedLetter = (pattern) =>
    Boolean(pattern && !/^-+$/u.test(pattern));

/**
 * First filled coordinate whose word matches (for clues popover hint pattern).
 */
export const findFilledCoordByWord = (filledCoordinates, word) => {
    if (!filledCoordinates?.length || !word) {
        return null;
    }
    const target = normalizeWord(word);
    return filledCoordinates.find((c) => normalizeWord(c.text || c.word || "") === target) ?? null;
};

/**
 * Calculates the progress percentage of found words
 * @param {number} wordsFoundCount - Number of words found
 * @param {number} totalWordsCount - Total number of words in puzzle
 * @returns {number} - Progress percentage (0-100)
 */
export const calculateProgress = (wordsFoundCount, totalWordsCount) => {
    if (!totalWordsCount || totalWordsCount === 0) {
        return 0;
    }
    return Math.round((wordsFoundCount / totalWordsCount) * 100);
};

/**
 * Splits a single storage line into words (API may use real newlines or literal "\\n").
 * @param {string} line
 * @returns {string[]}
 */
const splitWordsFoundLine = (line) => {
    if (typeof line !== 'string' || !line.trim()) {
        return [];
    }
    // Some APIs store newline as the two characters \ and n instead of an actual newline
    const normalized = line.replace(/\r\n/g, '\n').replace(/\\n/g, '\n');
    return normalized
        .split(/\r\n|\r|\n/g)
        .map((w) => w.trim())
        .filter((w) => w.length > 0)
        .map((w) => w.toLowerCase());
};

/**
 * Parses wordsFound string from API response (format: "word1\nword2\nword3")
 * Also handles arrays where one element is "word1\nword2" (newline inside a single string).
 * @param {string|Array} wordsFound - The wordsFound field from API (string or array)
 * @returns {Array<string>} - Array of normalized words
 */
export const parseWordsFound = (wordsFound) => {
    if (wordsFound == null) {
        return [];
    }

    if (typeof wordsFound === 'string') {
        return splitWordsFoundLine(wordsFound);
    }

    if (Array.isArray(wordsFound)) {
        return wordsFound.flatMap((item) => {
            if (item == null) return [];
            if (typeof item === 'string') {
                return splitWordsFoundLine(item);
            }
            return splitWordsFoundLine(String(item));
        });
    }

    return [];
};

